'use client';

import { Fragment, useEffect, useState } from 'react';
import Slider from 'react-slick';
import { twMerge } from 'tailwind-merge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import { RichText } from '@lamesarv-sdk/components';

import { IComponentSearchResultsBanner } from '@/types/cms';

import { CommonImage } from '../CommonImage';

interface IResultsBannerOverlayProps {
  overlay_text?: string;
  overlay_cta_text?: string;
  overlay_cta_link?: string;
  classes?: string;
  classes_overlay_text?: string;
  classes_overlay_cta?: string;
  classes_extra?: string;
}

const ResultsBannerOverlay = (props: IResultsBannerOverlayProps) => {
  if (!props.overlay_text && !props.overlay_cta_text && !props.overlay_cta_link) return null;

  return (
    <Fragment>
      <div className="absolute inset-0 bg-black opacity-20"></div>
      <div
        className={twMerge(
          'absolute left-14 right-14 top-8 bottom-8 flex flex-col justify-start text-white font-helvetica z-20',
          props.classes,
          props.classes_extra,
        )}
      >
        {!!props.overlay_text && (
          <h2 className={twMerge('text-[16px] md:text-[24px] font-bold', props.classes_overlay_text)}>
            <RichText content={props.overlay_text} className="[&_p]:m-0 leading-8" />
          </h2>
        )}
        {!!props.overlay_cta_text && !!props.overlay_cta_link && (
          <a
            href={props.overlay_cta_link || '#'}
            className={twMerge('underline text-[16px] md:text-[24px] self-start', props.classes_overlay_cta)}
          >
            {props.overlay_cta_text}
          </a>
        )}
      </div>
    </Fragment>
  );
};

interface ICustomArrowProps {
  isRight?: boolean;
  onClick?: () => void;
}

const CustomArrow = ({ isRight, onClick }: ICustomArrowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={twMerge(
      'absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer z-20 outline-none text-white hover:text-blue-50 inventory-item-sub-button',
      isRight ? 'right-3' : 'left-3',
    )}
    data-testid={`${isRight ? 'right' : 'left'}-arrow`}
  >
    <FontAwesomeIcon
      className="size-8 drop-shadow-[0px_0px_3px_#00000080]"
      icon={isRight ? faChevronRight : faChevronLeft}
    />
  </button>
);

const CustomPaging = () => <figure className="size-1.5 bg-[#98A2A3] rounded-full" />;

interface IResultsBannerProps {
  classes?: string;
  customSessionKey?: string;
  data_tag?: string;
  data_tag_group?: string;
  disable_pause_on_hover?: boolean;
  display_arrows?: string;
  display_dots?: string;
  images: IComponentSearchResultsBanner[];
  speed?: number;
}

export const ResultsBanner = ({
  classes,
  customSessionKey,
  data_tag,
  data_tag_group,
  disable_pause_on_hover,
  display_arrows,
  display_dots,
  images,
  speed,
}: IResultsBannerProps) => {
  const [isDismissed, setIsDismissed] = useState(true);
  const autoplaySpeed = speed ? speed * 1000 : undefined;

  const sessionKey = `lamesarv::dismissed-${customSessionKey || 'resultsBanner'}`;

  const onDismiss = () => {
    setIsDismissed(true);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(sessionKey, 'true');
    }
  };

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') {
      setIsDismissed(true);
      return;
    }

    const sessionVisible = sessionStorage.getItem(sessionKey);
    setIsDismissed(!!sessionVisible);
  }, []);

  if (!images || images.length === 0) return null;

  const displayArrows =
    display_arrows === undefined || display_arrows === 'default' ? images.length > 1 : display_arrows === 'true';
  const displayDots =
    display_dots === undefined || display_dots === 'default' ? images.length > 1 : display_dots === 'true';

  return !isDismissed ? (
    <div
      className={twMerge('relative w-full px-2', classes)}
      data-tag={data_tag ? `${data_tag}--results-banner` : 'results-banner'}
      data-tag-group={data_tag_group || 'search-results'}
    >
      <Slider
        slidesToScroll={1}
        slidesToShow={1}
        infinite={images.length > 1}
        arrows={displayArrows}
        nextArrow={<CustomArrow isRight />}
        prevArrow={<CustomArrow />}
        dots={displayDots}
        pauseOnHover={disable_pause_on_hover ? false : true}
        dotsClass="bg-[#0F060399] !flex items-center gap-2 absolute bottom-4 left-1/2 -translate-x-1/2 px-1 py-0.5 rounded-full [&_.slick-active_figure]:bg-white [&_.slick-active_figure]:size-2 z-20"
        customPaging={CustomPaging}
        adaptiveHeight
        autoplay
        autoplaySpeed={autoplaySpeed || 3000}
      >
        {images.map((image, index) => {
          return (
            <div
              key={image.alt + index}
              className="relative rounded-xl md:rounded lg:rounded-[0.625rem] overflow-hidden"
            >
              <CommonImage
                src={image.mobile_image_url}
                alt={image.alt}
                className="size-full object-cover max-h-[200px] min-[430px]:hidden"
              />
              <CommonImage
                src={image.mobile_large_image_url}
                alt={image.alt}
                className="size-full object-cover max-h-[200px] max-[429px]:hidden md:hidden"
              />
              <CommonImage
                src={image.tablet_image_url}
                alt={image.alt}
                className="size-full object-cover max-h-[200px] max-md:hidden lg:hidden"
              />
              <CommonImage
                src={image.desktop_small_image_url}
                alt={image.alt}
                className="size-full object-cover max-h-[200px] max-lg:hidden min-[1440px]:hidden"
              />
              <CommonImage
                src={image.desktop_medium_image_url}
                alt={image.alt}
                className="size-full object-cover max-h-[200px] max-[1439px]:hidden min-[1920px]:hidden"
              />
              <CommonImage
                src={image.desktop_large_image_url}
                alt={image.alt}
                className="size-full object-cover max-h-[200px] max-[1919px]:hidden"
              />
              <ResultsBannerOverlay
                overlay_text={image.mobile_overlay_text}
                overlay_cta_text={image.mobile_overlay_cta_text}
                overlay_cta_link={image.mobile_overlay_cta_link}
                classes={image.classes_overlay}
                classes_overlay_text={image.classes_overlay_text}
                classes_overlay_cta={image.classes_overlay_cta}
                classes_extra="md:hidden"
              />
              <ResultsBannerOverlay
                overlay_text={image.tablet_overlay_text}
                overlay_cta_text={image.tablet_overlay_cta_text}
                overlay_cta_link={image.tablet_overlay_cta_link}
                classes={image.classes_overlay}
                classes_overlay_text={image.classes_overlay_text}
                classes_overlay_cta={image.classes_overlay_cta}
                classes_extra="max-md:hidden lg:hidden"
              />
              <ResultsBannerOverlay
                overlay_text={image.desktop_overlay_text}
                overlay_cta_text={image.desktop_overlay_cta_text}
                overlay_cta_link={image.desktop_overlay_cta_link}
                classes={image.classes_overlay}
                classes_overlay_text={image.classes_overlay_text}
                classes_overlay_cta={image.classes_overlay_cta}
                classes_extra="max-lg:hidden"
              />
            </div>
          );
        })}
      </Slider>
      <div className="absolute right-6 top-4">
        <span className="cursor-pointer" onClick={onDismiss}>
          <FontAwesomeIcon className="size-6 text-white" icon={faXmark} />
        </span>
      </div>
    </div>
  ) : null;
};
